import {
  collection,
  addDoc,
  serverTimestamp,
  Timestamp,
  doc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  limit,
  deleteDoc,
  setDoc
} from 'firebase/firestore';
import {
  addVendorToSharePoint,
  updateVendorStatusInSharePoint,
  initializeSharePoint,
  ensureVendorListExists,
  migrateVendorToSharePoint
} from './sharepointService';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL,
  getBlob,
  deleteObject
} from 'firebase/storage';
import { db, storage } from './firebase';
import { logAuditEvent } from './staffPlatformService';

// Sanitize company name for use in storage paths
const sanitizeForPath = (name: string): string => {
  return (name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 80); // keep reasonable length
};

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB per document

export interface VendorFormData {
  // Business Registration Details
  companyName: string;
  telephone: string;
  fax: string;
  email: string;
  registeredAddress: string;
  ownersPartners: string;
  ownerAddress: string;
  registrationNo: string;
  registrationDate: string;
  tinNo: string;
  isLimitedLiability: string;
  paidUpCapital: string;
  lastTurnover: string;
  isISO9001: string;
  
  // Categories
  categories: {
    agriculturalConsulting: boolean;
    equipmentCalibration: boolean;
    technicalSupplies: boolean;
    nonTechnicalSupplies: boolean;
    insuranceServices: boolean;
    automobileServices: boolean;
    cateringServices: boolean;
    interiorDecoration: boolean;
    civilElectricalWorks: boolean;
    manpowerSupply: boolean;
    others: boolean;
    othersSpecify: string;
  };
  
  specializationDetails: string;
  extendCredit: string;
  creditVolume: string;
  creditTenure: string;
  paymentPlan: string;
  postDeliveryPayment: string;
  
  // Banking Information
  bankingInfo: Array<{
    bankName: string;
    accountType: string;
    accountNumber: string;
  }>;
  bvn: string;
  bankDiscussionObjection: string;
  bankObjectionReason: string;
  
  // References
  references: Array<{
    company: string;
    telNo: string;
    date: string;
    location: string;
    contractValue: string;
    clientContact: string;
  }>;
  performanceDiscussionObjection: string;
  performanceObjectionReason: string;
  
  // Authorized Representative Details
  authorizedRepresentativeName: string;
  authorizedRepresentativePhone: string;
  authorizedRepresentativeDesignation: string;
  // Contact Person Details
  contactPersonName: string;
  contactPersonPhone: string;
  contactPersonEmail: string;
  declarationAccepted: boolean;
}

export interface UploadedDocuments {
  applicationLetter: File | null;
  incorporationCert: File | null;
  vatCert: File | null;
  passportPhoto: File | null;
  previousJobEvidence: File | null;
  taxClearance: File | null;
  performanceBond: File | null;
  referenceLetter: File | null;
  authorizedRepresentativeSignature: File | null;
  memat: File | null;
  identityDocuments: File | null;
  utilityBill: File | null;
  cacStatusReport: File | null;
  // Additional optional uploads
  letterOfIntroduction: File | null;
  boardResolution: File | null;
  companyProfile: File | null;
  taxComplianceCertificate: File | null;
  exportImportLicense: File | null;
  cacForm2And7: File | null;
  iso9001Certificate: File | null;
  productComplianceCertificates: File | null;
  hsePolicy: File | null;
  safetyRecords: File | null;
  generalLiabilityInsurance: File | null;
  productLiabilityInsurance: File | null;
  supplierCodeOfConduct: File | null;
  antiBriberyPolicyAcknowledgment: File | null;
  dataProtectionPolicy: File | null;
}

// Upload a single document to Firebase Storage
const uploadDocument = async (
  file: File,
  vendorId: string,
  docType: string,
  companyName: string
): Promise<string> => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(`File too large for ${docType}. Maximum size is 5 MB.`);
  }
  const companyFolder = `${sanitizeForPath(companyName)}-${vendorId}`;
  const storageRef = ref(storage, `vendor-documents/${companyFolder}/${docType}/${file.name}`);
  const snapshot = await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(snapshot.ref);
  return downloadURL;
};

// Upload multiple documents and return URLs
const uploadDocuments = async (
  documents: UploadedDocuments,
  vendorId: string,
  companyName: string
): Promise<Record<string, string>> => {
  const uploadPromises: Array<{ key: string; promise: Promise<string> }> = [];
  
  Object.entries(documents).forEach(([docType, file]) => {
    if (file) {
      uploadPromises.push({
        key: docType,
        promise: uploadDocument(file, vendorId, docType, companyName)
      });
    }
  });
  
  const results = await Promise.all(uploadPromises.map(item => item.promise));
  const documentUrls: Record<string, string> = {};
  
  uploadPromises.forEach((item, index) => {
    documentUrls[item.key] = results[index];
  });
  
  return documentUrls;
};

// Submit vendor registration form
export const submitVendorRegistration = async (
  formData: VendorFormData, 
  documents: UploadedDocuments
): Promise<{ success: boolean; vendorId?: string; error?: string }> => {
  try {
    console.log('Submitting vendor registration...');
    
    // Create the vendor document in Firestore
    const vendorRef = await addDoc(collection(db, 'vendors'), {
      ...formData,
      status: 'pending',
      submittedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    const vendorId = vendorRef.id;
    console.log(`✓ Vendor document created with ID: ${vendorId}`);
    
    // Upload documents to Firebase Storage
    const documentUrls = await uploadDocuments(documents, vendorId, formData.companyName);
    console.log(`✓ Documents uploaded successfully`);
    
    // Update the vendor document with document URLs
    await updateDoc(doc(db, 'vendors', vendorId), {
      documentUrls,
      updatedAt: serverTimestamp()
    });
    
    console.log(`✓ Vendor registration completed successfully with ID: ${vendorId}`);

    await logAuditEvent({
      action: 'vendor.submitted',
      actorEmail: formData.email || 'vendor@unknown',
      actorName: formData.companyName || 'Vendor',
      targetType: 'vendor',
      targetId: vendorId,
      targetLabel: formData.companyName,
      details: 'New vendor registration submitted',
    });
    
    return { success: true, vendorId };
    
  } catch (error) {
    console.error('Error in submitVendorRegistration:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'An unknown error occurred' 
    };
  }
};

// Update an existing vendor registration (resubmission/edit)
export const updateVendorRegistration = async (
  vendorId: string,
  formData: VendorFormData,
  documents: Partial<UploadedDocuments>
): Promise<{ success: boolean; vendorId?: string; error?: string }> => {
  try {
    // Update basic fields
    await updateDoc(doc(db, 'vendors', vendorId), {
      ...formData,
      updatedAt: serverTimestamp()
    });

    // Upload newly provided documents and merge into documentUrls
    const newUrls: Record<string, string> = {};
    for (const [docType, file] of Object.entries(documents)) {
      if (file) {
        const url = await uploadDocument(file as File, vendorId, docType, formData.companyName);
        newUrls[docType] = url;
      }
    }

    if (Object.keys(newUrls).length > 0) {
      await updateDoc(doc(db, 'vendors', vendorId), {
        documentUrls: newUrls,
        updatedAt: serverTimestamp()
      });
    }

    await logAuditEvent({
      action: 'vendor.updated',
      actorEmail: formData.email || 'vendor@unknown',
      actorName: formData.companyName || 'Vendor',
      targetType: 'vendor',
      targetId: vendorId,
      targetLabel: formData.companyName,
      details: 'Vendor profile updated',
    });

    return { success: true, vendorId };
  } catch (error) {
    console.error('Error in updateVendorRegistration:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'An unknown error occurred' 
    };
  }
};

const getVendorTimestamp = (vendor: { submittedAt?: any; createdAt?: any; updatedAt?: any }) => {
  const date = vendor.submittedAt || vendor.createdAt || vendor.updatedAt;
  if (!date) return 0;
  try {
    return date.toDate ? date.toDate().getTime() : new Date(date).getTime();
  } catch {
    return 0;
  }
};

const normalizeVendorEmail = (vendor: Record<string, any>): string =>
  (vendor.email || vendor.contactPersonEmail || '').trim().toLowerCase();

const shouldIncludeVendorProfile = (data: Record<string, any>): boolean => {
  if (data.status || data.submittedAt) return true;
  if (data.profileComplete || data.declarationAccepted) return true;
  // Auth-flow vendors who submitted before status tracking was set correctly
  const hasDocuments = data.documentUrls && Object.keys(data.documentUrls).length > 0;
  if (data.uid && data.companyName && hasDocuments) return true;
  return false;
};

const normalizeVendorRecord = (id: string, data: Record<string, any>, source: string) => ({
  id,
  ...data,
  source,
  companyName: data.companyName || 'Unknown Company',
  email: data.email || data.contactPersonEmail || '',
  status: data.status || 'pending',
});

const dedupeVendors = (vendors: any[]) => {
  const byKey = new Map<string, any>();

  vendors.forEach((vendor) => {
    const emailKey = normalizeVendorEmail(vendor);
    const key = emailKey || `id:${vendor.id}:${vendor.source}`;

    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, vendor);
      return;
    }

    const existingScore =
      (existing.submittedAt ? 2 : 0) +
      (existing.status ? 1 : 0) +
      (existing.source === 'vendor-profiles' ? 1 : 0);
    const vendorScore =
      (vendor.submittedAt ? 2 : 0) +
      (vendor.status ? 1 : 0) +
      (vendor.source === 'vendor-profiles' ? 1 : 0);

    if (vendorScore > existingScore || getVendorTimestamp(vendor) > getVendorTimestamp(existing)) {
      byKey.set(key, vendor);
    }
  });

  return Array.from(byKey.values());
};

const fetchCollectionVendors = async (collectionName: string, source: string, status?: string) => {
  const snapshot = await getDocs(collection(db, collectionName));
  const vendors: any[] = [];

  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data();

    if (source === 'vendor-profiles' && !shouldIncludeVendorProfile(data)) {
      return;
    }

    const effectiveStatus = data.status || 'pending';
    if (status && effectiveStatus !== status) {
      return;
    }

    vendors.push(normalizeVendorRecord(docSnap.id, data, source));
  });

  return vendors;
};

export type GetAllVendorsResult =
  | { success: true; vendors: any[]; warnings?: string[] }
  | { success: false; error: string };

// Get all vendors with optional filtering (from both 'vendors' and 'vendor-profiles' collections)
export const getAllVendors = async (status?: string): Promise<GetAllVendorsResult> => {
  try {
    const allVendors: any[] = [];
    const fetchErrors: string[] = [];

    try {
      const legacyVendors = await fetchCollectionVendors('vendors', 'vendors', status);
      allVendors.push(...legacyVendors);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.warn('Error fetching from vendors collection:', error);
      fetchErrors.push(`vendors: ${message}`);
    }

    try {
      const profileVendors = await fetchCollectionVendors('vendor-profiles', 'vendor-profiles', status);
      allVendors.push(...profileVendors);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.warn('Error fetching from vendor-profiles collection:', error);
      fetchErrors.push(`vendor-profiles: ${message}`);
    }

    const uniqueVendors = dedupeVendors(allVendors);

    uniqueVendors.sort((a, b) => getVendorTimestamp(b) - getVendorTimestamp(a));

    return {
      success: true,
      vendors: uniqueVendors,
      ...(fetchErrors.length > 0 ? { warnings: fetchErrors } : {}),
    };
  } catch (error) {
    console.error('Error fetching vendors:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch vendors',
    };
  }
};

// Get vendor by ID (checks both 'vendors' and 'vendor-profiles' collections)
export const getVendorById = async (vendorId: string) => {
  try {
    // First try 'vendors' collection
    const vendorDoc = await getDoc(doc(db, 'vendors', vendorId));
    
    if (vendorDoc.exists()) {
      const vendorData = { id: vendorDoc.id, ...vendorDoc.data() };
      // Apply migration for backward compatibility
      const migratedData = migrateVendorFieldNames(vendorData);
      return { 
        success: true, 
        vendor: migratedData
      };
    }
    
    // If not found, try 'vendor-profiles' collection
    const profileDoc = await getDoc(doc(db, 'vendor-profiles', vendorId));
    
    if (profileDoc.exists()) {
      const vendorData = { id: profileDoc.id, ...profileDoc.data() };
      // Apply migration for backward compatibility
      const migratedData = migrateVendorFieldNames(vendorData);
      return { 
        success: true, 
        vendor: migratedData
      };
    }
    
    return { 
      success: false, 
      error: 'Vendor not found' 
    };
  } catch (error) {
    console.error('Error fetching vendor:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to fetch vendor' 
    };
  }
};

// Create vendor profile (linked to Firebase Auth UID)
export const createVendorProfile = async (profileData: {
  companyName: string;
  email: string;
  telephone: string;
}) => {
  try {
    // This should be called after Firebase Auth account is created
    // The UID will be available from the auth context
    // For now, we'll create a profile that can be linked later
    const profileRef = await addDoc(collection(db, 'vendor-profiles'), {
      ...profileData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      profileComplete: false
    });
    
    return { success: true, profileId: profileRef.id };
  } catch (error) {
    console.error('Error creating vendor profile:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to create vendor profile' 
    };
  }
};

// Link vendor profile to Firebase Auth UID
export const linkVendorProfileToAuth = async (uid: string, profileId: string) => {
  try {
    await updateDoc(doc(db, 'vendor-profiles', profileId), {
      uid,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error linking vendor profile:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to link vendor profile' 
    };
  }
};

// Migration helper: Convert old field names to new field names for backward compatibility
const migrateVendorFieldNames = (vendorData: any): any => {
  if (!vendorData) return vendorData;
  
  const migrated = { ...vendorData };
  
  // Migrate authorized representative fields (old: contactPersonName/Phone/Designation)
  // Only migrate if new fields don't exist and old fields do
  if (!migrated.authorizedRepresentativeName && migrated.contactPersonName) {
    migrated.authorizedRepresentativeName = migrated.contactPersonName;
  }
  if (!migrated.authorizedRepresentativePhone && migrated.contactPersonPhone) {
    migrated.authorizedRepresentativePhone = migrated.contactPersonPhone;
  }
  if (!migrated.authorizedRepresentativeDesignation && migrated.contactPersonDesignation) {
    migrated.authorizedRepresentativeDesignation = migrated.contactPersonDesignation;
  }
  
  // Migrate contact person fields (old: actualContactPersonName/Phone/Email)
  // Only migrate if new fields don't exist and old fields do
  if (!migrated.contactPersonName && migrated.actualContactPersonName) {
    migrated.contactPersonName = migrated.actualContactPersonName;
  }
  if (!migrated.contactPersonPhone && migrated.actualContactPersonPhone) {
    migrated.contactPersonPhone = migrated.actualContactPersonPhone;
  }
  if (!migrated.contactPersonEmail && migrated.actualContactPersonEmail) {
    migrated.contactPersonEmail = migrated.actualContactPersonEmail;
  }
  
  // Migrate signature field name in documentUrls
  if (migrated.documentUrls && typeof migrated.documentUrls === 'object') {
    if (!migrated.documentUrls.authorizedRepresentativeSignature && migrated.documentUrls.contactPersonSignature) {
      migrated.documentUrls.authorizedRepresentativeSignature = migrated.documentUrls.contactPersonSignature;
    }
  }
  
  return migrated;
};

// Get vendor profile by Firebase Auth UID
export const getVendorByUid = async (uid: string) => {
  try {
    const q = query(collection(db, 'vendor-profiles'), where('uid', '==', uid), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const vendorData = { id: doc.id, ...doc.data() };
      // Apply migration for backward compatibility
      const migratedData = migrateVendorFieldNames(vendorData);
      return { 
        success: true, 
        vendor: migratedData
      };
    } else {
      // Fallback: check if vendor exists in old 'vendors' collection with this email
      // This is for backward compatibility
      return { 
        success: false, 
        error: 'Vendor profile not found' 
      };
    }
  } catch (error) {
    console.error('Error fetching vendor by UID:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to fetch vendor' 
    };
  }
};

// Create or update vendor profile with UID (used during registration)
export const createOrUpdateVendorProfileWithUid = async (
  uid: string,
  profileData: {
    companyName: string;
    email: string;
    telephone: string;
  }
) => {
  try {
    // Check if profile already exists
    const q = query(collection(db, 'vendor-profiles'), where('uid', '==', uid), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Update existing profile
      const docId = querySnapshot.docs[0].id;
      await updateDoc(doc(db, 'vendor-profiles', docId), {
        ...profileData,
        updatedAt: serverTimestamp()
      });
      return { success: true, profileId: docId, isNew: false };
    } else {
      // Create new profile
      const profileRef = await addDoc(collection(db, 'vendor-profiles'), {
        uid,
        ...profileData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        profileComplete: false
      });
      return { success: true, profileId: profileRef.id, isNew: true };
    }
  } catch (error) {
    console.error('Error creating/updating vendor profile:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to create vendor profile' 
    };
  }
};

// Update vendor profile data (for authenticated vendors)
export const updateVendorProfileData = async (
  uid: string,
  profileData: Partial<VendorFormData>,
  documents?: Partial<UploadedDocuments>
) => {
  try {
    // Find the vendor profile by UID
    const q = query(collection(db, 'vendor-profiles'), where('uid', '==', uid), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: false, error: 'Vendor profile not found' };
    }
    
    const profileDoc = querySnapshot.docs[0];
    const profileId = profileDoc.id;
    const existingData = profileDoc.data();
    
    // Upload new documents if provided
    let documentUrls: Record<string, string> = existingData.documentUrls || {};
    if (documents && Object.keys(documents).length > 0) {
      const companyName = profileData.companyName || existingData.companyName || 'vendor';
      // Filter out null/undefined values and convert to UploadedDocuments format
      const documentsToUpload: UploadedDocuments = {} as UploadedDocuments;
      Object.entries(documents).forEach(([key, value]) => {
        if (value && value instanceof File) {
          (documentsToUpload as any)[key] = value;
        }
      });
      
      if (Object.keys(documentsToUpload).length > 0) {
        const newUrls = await uploadDocuments(documentsToUpload, profileId, companyName);
        documentUrls = { ...documentUrls, ...newUrls };
      }
    }
    
    // Check if this is a complete submission using merged profile data
    const mergedProfile = {
      ...existingData,
      ...profileData,
      documentUrls,
    };

    const hasBankingInfo = Array.isArray(mergedProfile.bankingInfo)
      ? mergedProfile.bankingInfo.length > 0
      : Boolean(mergedProfile.bankingInfo);

    const isCompleteSubmission =
      Boolean(mergedProfile.companyName) &&
      Boolean(mergedProfile.registeredAddress) &&
      Boolean(mergedProfile.categories) &&
      hasBankingInfo &&
      Boolean(mergedProfile.authorizedRepresentativeName) &&
      Boolean(documentUrls && Object.keys(documentUrls).length > 0);

    const isExplicitSubmit = profileData.declarationAccepted === true;

    // Update the profile
    const updatePayload: any = {
      ...profileData,
      documentUrls,
      updatedAt: serverTimestamp(),
    };

    // Mark as pending review when the vendor completes or explicitly submits the application
    if ((isCompleteSubmission || isExplicitSubmit) && !existingData.status) {
      updatePayload.status = 'pending';
      updatePayload.submittedAt = serverTimestamp();
      updatePayload.profileComplete = true;
    }

    await updateDoc(doc(db, 'vendor-profiles', profileId), updatePayload);
    
    return { success: true, profileId };
  } catch (error) {
    console.error('Error updating vendor profile:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to update vendor profile' 
    };
  }
};

// Download file from Firebase Storage as File object
export const downloadFileFromFirebase = async (url: string, fileName: string): Promise<File> => {
  try {
    // Extract the file path from the Firebase Storage URL
    const urlParts = url.split('/o/')[1];
    const filePath = decodeURIComponent(urlParts.split('?')[0]);
    
    // Get file reference and download
    const fileRef = ref(storage, filePath);
    const blob = await getBlob(fileRef);
    
    return new File([blob], fileName, { type: blob.type });
  } catch (error) {
    console.error('Error downloading file from Firebase:', error);
    throw error;
  }
};

// Delete vendor and all associated files from Firebase
export const deleteVendorFromFirebase = async (
  vendorId: string,
  actorEmail?: string,
  actorName?: string
): Promise<void> => {
  try {
    console.log(`Starting deletion of vendor ${vendorId} and all associated files...`);
    
    // First, get the vendor document to access document URLs (check both collections)
    let vendorDoc = await getDoc(doc(db, 'vendors', vendorId));
    let collectionName = 'vendors';
    
    if (!vendorDoc.exists()) {
      // Try vendor-profiles collection
      vendorDoc = await getDoc(doc(db, 'vendor-profiles', vendorId));
      collectionName = 'vendor-profiles';
    }
    
    if (!vendorDoc.exists()) {
      console.log(`Vendor ${vendorId} not found in either collection, skipping deletion`);
      return;
    }
    
    const vendorData = vendorDoc.data();
    
    // Delete all files from Storage
    if (vendorData?.documentUrls) {
      const documentUrls = vendorData.documentUrls as Record<string, string>;
      const deletePromises: Promise<void>[] = [];
      
      // Delete each document file
      for (const [key, url] of Object.entries(documentUrls)) {
        if (url && url.includes('firebasestorage.googleapis.com')) {
          try {
            // Extract the file path from the URL
            const urlParts = url.split('/o/')[1];
            if (urlParts) {
              const filePath = decodeURIComponent(urlParts.split('?')[0]);
              const fileRef = ref(storage, filePath);
              
              // Wrap delete in a promise that handles "object-not-found" errors
              deletePromises.push(
                deleteObject(fileRef).catch((error: any) => {
                  // Skip "object-not-found" errors (files already deleted manually)
                  if (error?.code === 'storage/object-not-found') {
                    console.log(`File already deleted (skipping): ${filePath}`);
                    return; // Successfully skipped
                  }
                  // Re-throw other errors
                  throw error;
                })
              );
              console.log(`Queued deletion of file: ${filePath}`);
            }
          } catch (urlError) {
            console.warn(`Failed to parse URL for deletion: ${url}`, urlError);
          }
        }
      }
      
      // Execute all file deletions
      if (deletePromises.length > 0) {
        console.log(`Deleting ${deletePromises.length} files from Storage...`);
        const results = await Promise.allSettled(deletePromises);
        const successful = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;
        console.log(`File deletion completed: ${successful} successful, ${failed} failed (some may have been already deleted)`);
      }
    }
    
    // Also try to delete the entire vendor folder as a fallback
    try {
      const companyName: string | undefined = (vendorData as any)?.companyName;
      if (companyName) {
        const companyFolder = `${sanitizeForPath(companyName)}-${vendorId}`;
        const newFolderRef = ref(storage, `vendor-documents/${companyFolder}`);
        try {
          await deleteObject(newFolderRef);
          console.log(`Deleted vendor folder: vendor-documents/${companyFolder}`);
        } catch (folderError: any) {
          // Skip "object-not-found" errors (folder already deleted manually)
          if (folderError?.code === 'storage/object-not-found') {
            console.log(`Vendor folder already deleted (skipping): vendor-documents/${companyFolder}`);
          } else {
            throw folderError;
          }
        }
      }
      // Backward compatibility: try old folder path by vendorId only
      try {
        const oldFolderRef = ref(storage, `vendor-documents/${vendorId}`);
        try {
          await deleteObject(oldFolderRef);
          console.log(`Deleted legacy vendor folder: vendor-documents/${vendorId}`);
        } catch (folderError: any) {
          // Skip "object-not-found" errors (folder already deleted manually)
          if (folderError?.code === 'storage/object-not-found') {
            console.log(`Legacy vendor folder already deleted (skipping): vendor-documents/${vendorId}`);
          }
          // Silently ignore other errors for backward compatibility
        }
      } catch {}
    } catch (folderError: any) {
      // Skip "object-not-found" errors
      if (folderError?.code === 'storage/object-not-found') {
        console.log(`Vendor folder deletion skipped (already deleted): ${folderError.message}`);
      } else {
        // Log other errors but don't fail the deletion
        console.log(`Vendor folder deletion skipped (may not exist): ${folderError}`);
      }
    }
    
    // Finally, delete the vendor document from Firestore
    await deleteDoc(doc(db, collectionName, vendorId));
    console.log(`Vendor document ${vendorId} deleted from ${collectionName} collection`);

    if (actorEmail) {
      await logAuditEvent({
        action: 'vendor.deleted',
        actorEmail,
        actorName: actorName || actorEmail,
        targetType: 'vendor',
        targetId: vendorId,
        targetLabel: vendorData?.companyName || vendorId,
        details: 'Vendor deleted from the platform',
      });
    }
    
    console.log(`✅ Vendor ${vendorId} and all associated files deleted successfully`);
  } catch (error) {
    console.error('Error deleting vendor from Firebase:', error);
    throw error;
  }
};

// Update vendor status (approve/reject) with SharePoint sync
export const updateVendorStatus = async (
  vendorId: string, 
  status: 'approved' | 'rejected' | 'pending',
  notes?: string,
  approvedBy?: string,
  accessToken?: string
) => {
  try {
    // Determine which collection the vendor is in
    let vendorDocRef = doc(db, 'vendors', vendorId);
    let vendorDoc = await getDoc(vendorDocRef);
    let collectionName = 'vendors';
    
    if (!vendorDoc.exists()) {
      // Try vendor-profiles collection
      vendorDocRef = doc(db, 'vendor-profiles', vendorId);
      vendorDoc = await getDoc(vendorDocRef);
      collectionName = 'vendor-profiles';
    }
    
    if (!vendorDoc.exists()) {
      return { 
        success: false, 
        error: 'Vendor not found in either collection' 
      };
    }

    // Update Firebase first
    const updateData: any = {
      status,
      notes: notes || '',
      approvedBy: approvedBy || '',
      updatedAt: serverTimestamp(),
      reviewedAt: serverTimestamp()
    };

    // Set initial SharePoint sync status for approved vendors
    if (status === 'approved') {
      updateData.sharePointSyncStatus = 'pending';
    }

    await updateDoc(vendorDocRef, updateData);

    const vendorData = vendorDoc.data();
    const companyName = vendorData?.companyName || vendorId;

    if (approvedBy) {
      await logAuditEvent({
        action: status === 'approved' ? 'vendor.approved' : status === 'rejected' ? 'vendor.rejected' : 'vendor.updated',
        actorEmail: approvedBy.includes('@') ? approvedBy : `${approvedBy}@staff`,
        actorName: approvedBy,
        targetType: 'vendor',
        targetId: vendorId,
        targetLabel: companyName,
        details: notes || `Vendor status changed to ${status}`,
        metadata: { status, notes },
      });
    }

    // If approved and SharePoint is configured, migrate to SharePoint
    if (status === 'approved' && accessToken && approvedBy) {
      try {
        // Initialize SharePoint
        await initializeSharePoint(accessToken);
        await ensureVendorListExists();

        // Get the full vendor data
        const vendorResult = await getVendorById(vendorId);
        if (vendorResult.success && vendorResult.vendor) {
          const vendor = vendorResult.vendor as any; // Type assertion for vendor with documentUrls
          
          // Download files from Firebase Storage
          const firebaseFiles: Record<string, File> = {};
          if (vendor.documentUrls) {
            console.log('Downloading files from Firebase Storage...');
            for (const [docType, url] of Object.entries(vendor.documentUrls)) {
              try {
                const urlString = url as string;
                const fileName = `${docType}_${vendorId}.${urlString.split('.').pop()?.split('?')[0] || 'pdf'}`;
                const file = await downloadFileFromFirebase(urlString, fileName);
                firebaseFiles[docType] = file;
                console.log(`✓ Downloaded ${docType}: ${fileName}`);
              } catch (downloadError) {
                console.warn(`Failed to download ${docType}:`, downloadError);
                // Continue with other files even if one fails
              }
            }
          }

          // Migrate vendor data and files to SharePoint
          const migrationResult = await migrateVendorToSharePoint(
            vendor,
            approvedBy,
            firebaseFiles
          );

          if (migrationResult.success) {
            console.log('Vendor migrated to SharePoint successfully');
            
            // Update sync status to synced
            await updateDoc(vendorDocRef, {
              sharePointSyncStatus: 'synced',
              sharePointSyncDate: serverTimestamp(),
              sharePointSyncBy: approvedBy,
              updatedAt: serverTimestamp()
            });
            
            // Delete vendor data and files from Firebase after successful migration
            // Only delete from 'vendors' collection (legacy), keep vendor-profiles
            // COMMENTED OUT: Keeping vendor data in Firebase after SharePoint sync
            // if (collectionName === 'vendors') {
            //   try {
            //     await deleteVendorFromFirebase(vendorId);
            //     console.log('Vendor data deleted from Firebase after successful SharePoint migration');
            //   } catch (deleteError) {
            //     console.warn('Failed to delete vendor from Firebase:', deleteError);
            //     // Don't fail the approval process if cleanup fails
            //   }
            // }
          } else {
            console.warn('SharePoint migration failed:', migrationResult.error);
            
            // Update sync status to failed
            await updateDoc(vendorDocRef, {
              sharePointSyncStatus: 'failed',
              sharePointSyncError: migrationResult.error,
              sharePointSyncDate: serverTimestamp(),
              sharePointSyncBy: approvedBy,
              updatedAt: serverTimestamp()
            });
            
            // Keep the vendor in Firebase if SharePoint migration fails
          }
        }
      } catch (sharePointError) {
        console.warn('SharePoint sync error:', sharePointError);
        
        // Update sync status to failed
        try {
          await updateDoc(vendorDocRef, {
            sharePointSyncStatus: 'failed',
            sharePointSyncError: sharePointError instanceof Error ? sharePointError.message : 'Unknown error',
            sharePointSyncDate: serverTimestamp(),
            sharePointSyncBy: approvedBy || 'System',
            updatedAt: serverTimestamp()
          });
        } catch (updateError) {
          console.warn('Failed to update vendor sync status:', updateError);
        }
        
        // Don't fail the entire operation, just log the warning
      }
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error updating vendor status:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to update vendor status' 
    };
  }
};

export type GetVendorStatsResult =
  | { success: true; stats: { total: number; pending: number; approved: number; rejected: number; categories: Record<string, number> } }
  | { success: false; error: string };

// Get vendor statistics
export const getVendorStats = async (): Promise<GetVendorStatsResult> => {
  try {
    const allVendors = await getAllVendors();
    
    if (!allVendors.success) {
      return { success: false, error: allVendors.error };
    }
    
    const vendors = allVendors.vendors || [];
    const stats = {
      total: vendors.length,
      pending: vendors.filter((v: any) => v.status === 'pending').length,
      approved: vendors.filter((v: any) => v.status === 'approved').length,
      rejected: vendors.filter((v: any) => v.status === 'rejected').length,
      categories: {} as Record<string, number>
    };
    
    // Count by categories
    vendors.forEach((vendor: any) => {
      if (vendor.categories) {
        Object.keys(vendor.categories).forEach(category => {
          if (vendor.categories[category] && category !== 'othersSpecify') {
            stats.categories[category] = (stats.categories[category] || 0) + 1;
          }
        });
      }
    });
    
    return { success: true, stats };
  } catch (error) {
    console.error('Error fetching vendor stats:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to fetch vendor statistics' 
    };
  }
};

// Re-attempt SharePoint sync for a specific approved vendor
export const reattemptSharePointSync = async (
  vendorId: string,
  accessToken: string,
  approvedBy: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log(`Re-attempting SharePoint sync for vendor: ${vendorId}`);
    
    // Determine which collection the vendor is in
    let vendorDocRef = doc(db, 'vendors', vendorId);
    let vendorDoc = await getDoc(vendorDocRef);
    let collectionName = 'vendors';
    
    if (!vendorDoc.exists()) {
      // Try vendor-profiles collection
      vendorDocRef = doc(db, 'vendor-profiles', vendorId);
      vendorDoc = await getDoc(vendorDocRef);
      collectionName = 'vendor-profiles';
    }
    
    if (!vendorDoc.exists()) {
      return { success: false, error: 'Vendor not found in either collection' };
    }
    
    // Get the vendor data (with migration applied)
    const vendorResult = await getVendorById(vendorId);
    if (!vendorResult.success || !vendorResult.vendor) {
      return { success: false, error: 'Vendor not found' };
    }

    const vendor = vendorResult.vendor as any;
    
    // Check if vendor is approved
    if (vendor.status !== 'approved') {
      return { success: false, error: 'Vendor is not approved' };
    }

    // Initialize SharePoint
    await initializeSharePoint(accessToken);
    await ensureVendorListExists();

    // Download files from Firebase Storage
    const firebaseFiles: Record<string, File> = {};
    if (vendor.documentUrls) {
      console.log('Downloading files from Firebase Storage for re-sync...');
      for (const [docType, url] of Object.entries(vendor.documentUrls)) {
        try {
          const urlString = url as string;
          const fileName = `${docType}_${vendorId}.${urlString.split('.').pop()?.split('?')[0] || 'pdf'}`;
          const file = await downloadFileFromFirebase(urlString, fileName);
          firebaseFiles[docType] = file;
          console.log(`✓ Downloaded ${docType}: ${fileName}`);
        } catch (downloadError) {
          console.warn(`Failed to download ${docType}:`, downloadError);
          // Continue with other files even if one fails
        }
      }
    }

    // Migrate vendor data and files to SharePoint
    const migrationResult = await migrateVendorToSharePoint(
      vendor,
      approvedBy,
      firebaseFiles
    );

    if (migrationResult.success) {
      console.log('Vendor re-synced to SharePoint successfully');
      
      // Update vendor with sync status (use the correct collection)
      await updateDoc(vendorDocRef, {
        sharePointSyncStatus: 'synced',
        sharePointSyncDate: serverTimestamp(),
        sharePointSyncBy: approvedBy,
        updatedAt: serverTimestamp()
      });
      
      // Delete vendor data and files from Firebase after successful migration
      // COMMENTED OUT: Keeping vendor data in Firebase after SharePoint re-sync
      // try {
      //   await deleteVendorFromFirebase(vendorId);
      //   console.log('Vendor data deleted from Firebase after successful SharePoint re-sync');
      // } catch (deleteError) {
      //   console.warn('Failed to delete vendor from Firebase:', deleteError);
      //   // Don't fail the sync process if cleanup fails
      // }
      
      return { success: true };
    } else {
      console.warn('SharePoint re-sync failed:', migrationResult.error);
      
      // Update vendor with failed sync status (use the correct collection)
      await updateDoc(vendorDocRef, {
        sharePointSyncStatus: 'failed',
        sharePointSyncError: migrationResult.error,
        sharePointSyncDate: serverTimestamp(),
        sharePointSyncBy: approvedBy,
        updatedAt: serverTimestamp()
      });
      
      return { success: false, error: migrationResult.error };
    }
  } catch (error) {
    console.error('Error re-attempting SharePoint sync:', error);
    
    // Update vendor with failed sync status (try to determine collection if not already done)
    try {
      let vendorDocRef = doc(db, 'vendors', vendorId);
      let vendorDoc = await getDoc(vendorDocRef);
      
      if (!vendorDoc.exists()) {
        vendorDocRef = doc(db, 'vendor-profiles', vendorId);
        vendorDoc = await getDoc(vendorDocRef);
      }
      
      if (vendorDoc.exists()) {
        await updateDoc(vendorDocRef, {
          sharePointSyncStatus: 'failed',
          sharePointSyncError: error instanceof Error ? error.message : 'Unknown error',
          sharePointSyncDate: serverTimestamp(),
          sharePointSyncBy: approvedBy,
          updatedAt: serverTimestamp()
        });
      }
    } catch (updateError) {
      console.warn('Failed to update vendor sync status:', updateError);
    }
    
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Interface for other documents
export interface OtherDocument {
  id: string;
  name: string;
  fileName: string;
  url: string;
  uploadedAt: any; // Firestore Timestamp
  size?: number;
}

// Add an other document to vendor profile
export const addOtherDocument = async (
  uid: string,
  documentName: string,
  file: File
): Promise<{ success: boolean; document?: OtherDocument; error?: string }> => {
  try {
    // Find the vendor profile by UID
    const q = query(collection(db, 'vendor-profiles'), where('uid', '==', uid), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: false, error: 'Vendor profile not found' };
    }
    
    const profileDoc = querySnapshot.docs[0];
    const profileId = profileDoc.id;
    const existingData = profileDoc.data();
    const companyName = existingData.companyName || 'vendor';
    
    // Validate file size (5 MB limit)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return { success: false, error: 'File size exceeds 5 MB limit. Please upload a smaller file.' };
    }
    
    // Upload document to Firebase Storage
    const companyFolder = `${sanitizeForPath(companyName)}-${profileId}`;
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storageRef = ref(storage, `vendor-documents/${companyFolder}/other-documents/${timestamp}_${sanitizedFileName}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    // Create document object
    const newDocument: OtherDocument = {
      id: `${timestamp}_${sanitizedFileName}`,
      name: documentName.trim(),
      fileName: file.name,
      url: downloadURL,
      uploadedAt: Timestamp.now(),
      size: file.size
    };
    
    // Get existing other documents or initialize empty array
    const existingOtherDocuments: OtherDocument[] = existingData.otherDocuments || [];
    
    // Add new document to array
    const updatedOtherDocuments = [...existingOtherDocuments, newDocument];
    
    // Update vendor profile
    await updateDoc(doc(db, 'vendor-profiles', profileId), {
      otherDocuments: updatedOtherDocuments,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, document: newDocument };
  } catch (error) {
    console.error('Error adding other document:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to upload document' 
    };
  }
};

// Get all other documents for a vendor
export const getOtherDocuments = async (
  uid: string
): Promise<{ success: boolean; documents?: OtherDocument[]; error?: string }> => {
  try {
    const vendorResult = await getVendorByUid(uid);
    if (!vendorResult.success || !vendorResult.vendor) {
      return { success: false, error: 'Vendor profile not found' };
    }
    
    const otherDocuments: OtherDocument[] = vendorResult.vendor.otherDocuments || [];
    return { success: true, documents: otherDocuments };
  } catch (error) {
    console.error('Error getting other documents:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to retrieve documents' 
    };
  }
};

// Delete an other document from vendor profile
export const deleteOtherDocument = async (
  uid: string,
  documentId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Find the vendor profile by UID
    const q = query(collection(db, 'vendor-profiles'), where('uid', '==', uid), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: false, error: 'Vendor profile not found' };
    }
    
    const profileDoc = querySnapshot.docs[0];
    const profileId = profileDoc.id;
    const existingData = profileDoc.data();
    const otherDocuments: OtherDocument[] = existingData.otherDocuments || [];
    
    // Find the document to delete
    const documentToDelete = otherDocuments.find(doc => doc.id === documentId);
    if (!documentToDelete) {
      return { success: false, error: 'Document not found' };
    }
    
    // Delete file from Firebase Storage
    try {
      const urlParts = documentToDelete.url.split('/o/')[1];
      if (urlParts) {
        const filePath = decodeURIComponent(urlParts.split('?')[0]);
        const fileRef = ref(storage, filePath);
        await deleteObject(fileRef);
      }
    } catch (storageError) {
      console.warn('Error deleting file from storage (continuing with document deletion):', storageError);
      // Continue with document deletion even if storage deletion fails
    }
    
    // Remove document from array
    const updatedOtherDocuments = otherDocuments.filter(doc => doc.id !== documentId);
    
    // Update vendor profile
    await updateDoc(doc(db, 'vendor-profiles', profileId), {
      otherDocuments: updatedOtherDocuments,
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting other document:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to delete document' 
    };
  }
};